import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useRef, useState, useCallback } from "react";

interface ProductCardProps {
  title: string;
  description: string;
  image: string;
  href: string;
}

const ProductCard = ({ title, description, image, href }: ProductCardProps) => {
  const lang = useLanguage();
  const cardRef = useRef<HTMLElement>(null);
  const [pos, setPos] = useState({ x: -1000, y: -1000 });

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setPos({ x: -1000, y: -1000 });
  }, []);

  return (
    <article
      ref={cardRef}
      className="group relative rounded-lg"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Glow border overlay */}
      <div
        className="pointer-events-none absolute -inset-[1px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          background: `radial-gradient(
            250px circle at ${pos.x}px ${pos.y}px,
            hsl(38 60% 55% / 0.6),
            hsl(35 40% 75% / 0.3) 40%,
            transparent 70%
          )`,
        }}
      />
      {/* Card content */}
      <Link
        to={href}
        className="relative block bg-card rounded-lg overflow-hidden border border-border/60 hover:shadow-lg transition-shadow"
      >
        {/* Inner surface glow */}
        <div
          className="pointer-events-none absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"
          style={{
            background: `radial-gradient(
              300px circle at ${pos.x}px ${pos.y}px,
              hsl(38 60% 55% / 0.08),
              transparent 60%
            )`,
          }}
        />
        <div className="aspect-[4/3] overflow-hidden">
          <img
            src={image}
            alt={title}
            width={600}
            height={450}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
        <div className="p-5 relative z-20">
          <h3 className="font-display text-lg font-semibold mb-2">{title}</h3>
          <p className="text-muted-foreground text-sm leading-relaxed mb-3">{description}</p>
          <span
            className="inline-flex items-center gap-1 text-sm font-medium text-foreground group-hover:gap-2 transition-all"
            aria-hidden="true"
          >
            {lang === "en" ? "Read more" : "Lue lisää"} <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </Link>
    </article>
  );
};

export default ProductCard;
